import { Injectable, ViewContainerRef } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { InventoryType } from "../constants/app-constants";
import { RightPanelComponent } from "../components/right-panel/right-panel.component";
import { VendorServiceFormComponent } from "../components/operations/job-orders/vendor-service-form/vendor-service-form.component";
import { JobOrderVendorService } from "../models/job-orders.interface";
import { ToasterService } from "./toaster.service";
import { FormArray, FormGroup } from "@angular/forms";
import { ServiceType } from "../constants/master-constants";

export enum SERVICE_ACTION {
  ADD = "add",
  EDIT = "edit",
  VIEW = "view"
}

export function joinCompositeKey(parts: (string | number)[]): string {
  return parts.join(COMPOSITE_KEY_DELIMITER);
}

export const COMPOSITE_KEY_DELIMITER = '::';

@Injectable({
  providedIn: "root" 
})

export class JobOrderService {


    constructor(private toasterService: ToasterService) {

    }

    openServicePanel(
        viewContainerRef: ViewContainerRef,
        inventoryOptions: any[],
        inventoryType: InventoryType,
        serviceType: ServiceType,
        joborderVendor: JobOrderVendorService | null = null,
        onSaveCallback?: (jobOrderVendorService: JobOrderVendorService, appliedInventoryIds: string[]) => void,
      ) {
        const isEdit = !!joborderVendor;
        

        const rightPanelComponentRef = viewContainerRef.createComponent(RightPanelComponent);
        rightPanelComponentRef.instance.title = isEdit ? "Edit Service" : "Add Service";
        rightPanelComponentRef.instance.showActionBtns = true;

        const manageAttributeRef = rightPanelComponentRef.instance.loadComponent(VendorServiceFormComponent);
        manageAttributeRef.setInput("jobOrderVendorService", joborderVendor);
        manageAttributeRef.setInput("inventoryOptions", inventoryOptions);
        manageAttributeRef.setInput("inventoryType", inventoryType);
        manageAttributeRef.setInput("serviceType", serviceType);

        rightPanelComponentRef.instance.primaryActionBtnEmitter.subscribe(() => {
            manageAttributeRef.instance.saveForm();
        });

        manageAttributeRef.instance.onSave.subscribe((event: { jobOrderVendorService: JobOrderVendorService, appliedInventoryIds: string[] }) => {
            onSaveCallback?.(event.jobOrderVendorService, event.appliedInventoryIds);
            rightPanelComponentRef.destroy();
        });

        rightPanelComponentRef.instance.close.subscribe(() => rightPanelComponentRef.destroy());
    }

    addServiceToInventory(vendorMap: Map<String, JobOrderVendorService[]>, inventoryList: any[], jobOrderService: JobOrderVendorService, appliedInventoryIds: string[], inventoryType: InventoryType) {
      if (jobOrderService.attributes?.length) {
        for (let j = 0; j < jobOrderService.attributes?.length; j++) {
          const attribute = jobOrderService.attributes[j];
          attribute.displayValue = `${attribute.name} : ${attribute.values[0].value}`;
        }
      }
      appliedInventoryIds.forEach((inventoryId: string) => {
        const vendorList = vendorMap.get(inventoryId);
        if (vendorList) {
          const existingVendor = vendorList.find((vendor: JobOrderVendorService) => vendor.serviceId === jobOrderService.serviceId);
          if (existingVendor) {
            const inventoryNo = inventoryType === InventoryType.CARGO ? inventoryList.find((item: any) => item.id === inventoryId)?.igmItemNo : inventoryList.find((item: any) => item.id === inventoryId)?.containerNo;
            this.toasterService.error(`Service already added for the selected ${inventoryType === InventoryType.CARGO ? "cargo" : "container"}: ${inventoryNo}`);
            return;
          }
          vendorList.push(jobOrderService);
        } else {
          vendorMap.set(inventoryId, [jobOrderService]);
        }
      });
    }

    addServiceToCompositeInventory(
      vendorMap: Map<string, JobOrderVendorService[]>,
      inventoryList: any[],
      jobOrderService: JobOrderVendorService,
      appliedCompositeKeys: string[],
      inventoryType: InventoryType
    ) {
      // Optional: format displayValue for attributes
      if (jobOrderService.attributes?.length) {
        for (const attribute of jobOrderService.attributes) {
          attribute.displayValue = `${attribute.name} : ${attribute.values[0].value}`;
        }
      }
    
      appliedCompositeKeys.forEach((compositeKey) => {
        const { id, rest } = this.parseCompositeKey(compositeKey);
        const vendorList = vendorMap.get(compositeKey);
    
        if (vendorList) {
          const existingVendor = vendorList.find(v => v.serviceId === jobOrderService.serviceId);
          if (existingVendor) {
            // Find inventory number (could be cargo or container)
            const inventoryItem = inventoryList.find((item: any) => item.id === id);
            const inventoryNo = inventoryType === InventoryType.CARGO 
              ? inventoryItem?.documentNo  ?? inventoryItem?.igmItemNo
              : inventoryItem?.containerNo;
    
            // Optionally include truckNo / rest[0] in the message if present
            const extraInfo = rest.length > 0 ? ` (Details: ${rest.join(', ')})` : '';
    
            this.toasterService.error(
              `Service already added for the selected ${inventoryType === InventoryType.CARGO ? "cargo" : "container"}: ${inventoryNo}${extraInfo}`
            );
            return;
          }
          vendorList.push(jobOrderService);
        } else {
          vendorMap.set(compositeKey, [jobOrderService]);
        }
      });
    }
    

    fetchJORelatedInventories<T extends { inventoryId: string; inventoryType: string }>(
        jobOrderData: any,
        services: {
          containerService: (ids: string[]) => Observable<any>,
          cargoService: (ids: string[]) => Observable<any>
        }
      ): Observable<{ jobOrder: any, containers?: any, cargos?: any }> {
        const containerIds = jobOrderData.jobOrderInventoryList
          ?.filter((inv: T) => inv.inventoryType === "CONTAINER")
          ?.map((inv: T) => inv.inventoryId) ?? [];
      
        const cargoIds = jobOrderData.jobOrderInventoryList
          ?.filter((inv: T) => inv.inventoryType === "CARGO")
          ?.map((inv: T) => inv.inventoryId) ?? [];
      
        const forkJoinPayload: { jobOrder: Observable<any>, containers?: Observable<any>, cargos?: Observable<any> } = {
          jobOrder: of(jobOrderData)
        };
      
        if (containerIds.length) {
          forkJoinPayload.containers = services.containerService(containerIds);
        }
      
        if (cargoIds.length) {
          forkJoinPayload.cargos = services.cargoService(cargoIds);
        }
      
        return forkJoin(forkJoinPayload);
      }

    printFormErrors(formGroup: FormGroup | FormArray, path: string = ""): void {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            const controlPath = path ? `${path}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
            this.printFormErrors(control, controlPath);
            } else if (control && control.invalid) {
            console.log(`Control: ${controlPath}`, control.errors);
            }
        });
    }

    private parseCompositeKey(compositeKey: string): {
      id: string;
      rest: string[];
    } {
      const parts = compositeKey.split('::');
      const [id, ...rest] = parts;
      return { id, rest };
    }

}



